import React from 'react';
import { Shield } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white pt-24">
      {/* Hero Section */}
      <section className="relative py-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-white to-amber-50/20"></div>
        
        <div className="relative max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#F7931E] mb-4">
            <Shield className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B35] to-[#F7931E]">
            Privacy Policy
          </h1>
          <p className="text-lg text-[#5B6B7F]">
            Last updated: 2025-11-18
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          
          {/* 1. Introduction */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">1. Introduction</h2>
            <div className="text-[#5B6B7F] leading-relaxed">
              <p>
                This Privacy Policy explains how Coinexus Sp. z o.o. ("Coinexus", "we", "us", "the Company") processes and protects personal data in accordance with the General Data Protection Regulation (GDPR), Polish data-protection law, and AML/CTF regulatory requirements applicable to Virtual Asset Service Providers (VASPs).
              </p>
              <p className="mt-4">
                By using our services, you acknowledge that your data is processed as described in this Policy.
              </p>
            </div>
          </div>

          {/* 2. Data Controller */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">2. Data Controller</h2>
            <div className="text-[#5B6B7F] leading-relaxed space-y-2">
              <p className="font-semibold">Coinexus Sp. z o.o.</p>
              <p>
                Juliana Tuwima 48, unit 11,<br />
                90-021 Łódź, Poland
              </p>
              <p>
                KRS: 0001142506<br />
                NIP: 7252530538<br />
                REGON: 540348512
              </p>
              <p>
                Email: <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a>
              </p>
            </div>
          </div>

          {/* 3. Categories of Personal Data Processed */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">3. Categories of Personal Data Processed</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We process only the data necessary for providing regulated OTC exchange services, including:
            </p>
            
            <div className="space-y-4 ml-4">
              <div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">3.1 Identification Data</h3>
                <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
                  <li>Full name</li>
                  <li>Date of birth</li>
                  <li>Nationality</li>
                  <li>Government-issued ID or passport</li>
                  <li>Proof of address</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">3.2 Verification & Compliance Data</h3>
                <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
                  <li>Source-of-Funds / Source-of-Wealth documentation</li>
                  <li>Sanctions & PEP screening results</li>
                  <li>Risk assessment results</li>
                  <li>Enhanced Due Diligence (EDD) documentation</li>
                  <li>Transaction monitoring data</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">3.3 Communication Data</h3>
                <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
                  <li>Email address, phone number</li>
                  <li>Correspondence with our compliance team</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">3.4 Transaction Data</h3>
                <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
                  <li>Bank account details (client-owned only)</li>
                  <li>Transaction purpose & supporting documents</li>
                </ul>
              </div>
            </div>

            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              We do not collect unnecessary or excessive data.
            </p>
          </div>

          {/* 4. Legal Basis for Processing */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">4. Legal Basis for Processing</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus processes personal data based on:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Article 6(1)(c) GDPR – legal obligation under AML/CTF laws (EU 5AMLD/6AMLD, Polish AML Act)</li>
              <li>Article 6(1)(b) GDPR – performance of a service agreement</li>
              <li>Article 6(1)(f) GDPR – legitimate interest in ensuring secure and compliant operations</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Certain data may also be processed under:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Article 9(2)(g) GDPR – substantial public interest in preventing money laundering and terrorist financing</li>
            </ul>
          </div>

          {/* 5. Purpose of Processing */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">5. Purpose of Processing</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Personal data is processed strictly for:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Client onboarding and identity verification</li>
              <li>Sanctions and PEP screening</li>
              <li>Source-of-Funds / Source-of-Wealth checks</li>
              <li>Ongoing monitoring of transactions</li>
              <li>Fraud prevention and AML/CTF compliance</li>
              <li>Regulatory reporting duties</li>
              <li>Secure OTC transaction execution</li>
              <li>Communication with clients</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              Coinexus does not use client data for marketing, advertising, or profiling unrelated to compliance.
            </p>
          </div>

          {/* 6. Data Sharing */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">6. Data Sharing</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Data may be shared only when legally required, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Polish Financial Intelligence Unit (GIIF)</li>
              <li>Law-enforcement authorities</li>
              <li>Regulatory bodies</li>
              <li>Banking partners for transaction execution</li>
              <li>Compliance service providers (e.g., Allpass.ai for KYC/KYT)</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4">
              All partners operate under strict confidentiality and GDPR-compliant agreements.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus never sells or transfers data for commercial purposes.
            </p>
          </div>

          {/* 7. Data Storage & Security */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">7. Data Storage & Security</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              We apply industry-standard security protocols to protect data, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Encryption at rest and in transit</li>
              <li>Restricted access control</li>
              <li>Secure storage of ID documents</li>
              <li>Audit logs and monitoring</li>
              <li>Internal policies for data governance</li>
            </ul>
          </div>

          {/* 8. Data Retention Period */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">8. Data Retention Period</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              As required by EU 5AMLD / Polish AML Act, all AML/KYC data must be retained for:
            </p>
            <p className="text-[#5B6B7F] leading-relaxed font-semibold ml-4">
              5 years
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              after termination of the business relationship or completion of a transaction.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              After this period, data is securely deleted unless further retention is required by law.
            </p>
          </div>

          {/* 9. International Transfers */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">9. International Transfers</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus stores data within the European Economic Area (EEA).
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              If transfers outside the EEA occur (e.g., compliance partners), they are conducted only under:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Standard Contractual Clauses (SCCs)</li>
              <li>Adequacy decisions</li>
              <li>GDPR-compliant safeguards</li>
            </ul>
          </div>

          {/* 10. Client Rights Under GDPR */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">10. Client Rights Under GDPR</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Clients may request:
            </p>
            <ul className="list-disc list-inside space-y-2 text-[#5B6B7F] ml-4">
              <li>Access to their data</li>
              <li>Correction of inaccurate data</li>
              <li>Restriction of processing in specific cases</li>
              <li>A copy of their data ("data portability")</li>
            </ul>
            <p className="text-[#5B6B7F] leading-relaxed mt-4 font-semibold">
              Important:
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              Under AML regulations, Coinexus may not delete or erase compliance data during the legal retention period.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              Requests may be sent to: <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a>
            </p>
          </div>

          {/* 11. Cookies */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">11. Cookies</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              The Coinexus website uses only minimal technical cookies necessary for website functionality.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              We do not use advertising, tracking, or analytics cookies.
            </p>
          </div>

          {/* 12. Changes to This Policy */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">12. Changes to This Policy</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              Coinexus may update this Policy when required by regulatory changes.
            </p>
            <p className="text-[#5B6B7F] leading-relaxed">
              The latest version will always be published on our website with the updated date.
            </p>
          </div>

          {/* 13. Contact */}
          <div className="space-y-4">
            <h2 className="text-3xl font-bold text-[#1E3A5F]">13. Contact</h2>
            <p className="text-[#5B6B7F] leading-relaxed">
              For any privacy-related inquiries:<br />
              <a href="mailto:compliance@coinexus.vip" className="text-[#FF6B35] hover:underline font-medium">compliance@coinexus.vip</a>
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Privacy;